import { useRachelScope } from '../contexts/RachelScope.context';
import { RachelScope } from '../effects/rachelScope';
import useRachelChangesStore from '../store/rachelChanges.store';
import { RACHEL_EFFECT_MS } from '../styles/rachelEffects.styles';
import {
  ApplyRachelListTransitionParams,
  DiffRachelListItemsParams,
  UseRachelListTransitionParams,
} from './rachelEffects.types';

const SCROLL_SETTLE_MS = 300;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const animateRemoval = async (scope: RachelScope, removedKeys: string[]) => {
  const store = useRachelChangesStore.getState();

  store.requestScroll({ scope, keys: removedKeys });
  await wait(SCROLL_SETTLE_MS);
  store.markRemoved({ scope, keys: removedKeys });
  await wait(RACHEL_EFFECT_MS.rowExit);
  store.markRemoved({ scope, keys: [] });
};

const diffItems = <T>({ previousItems, nextItems, getKey }: DiffRachelListItemsParams<T>) => {
  const previousKeys = new Set(previousItems.map(getKey));
  const nextKeys = new Set(nextItems.map(getKey));

  return {
    addedItems: nextItems.filter((item) => !previousKeys.has(getKey(item))),
    removedKeys: [...previousKeys].filter((key) => !nextKeys.has(key)),
  };
};

const markAddedKeys = (scope: RachelScope, addedKeys: string[]) => {
  const store = useRachelChangesStore.getState();

  store.markAdded({ scope, keys: addedKeys });
  store.requestScroll({ scope, keys: addedKeys });
};

export const useRachelListTransition = <T>({
  items,
  getKey,
  onApplied,
  onAdded,
}: UseRachelListTransitionParams<T>) => {
  const scope = useRachelScope();

  const applyAndNotify = (apply: () => boolean) => {
    const isApplied = apply();
    if (isApplied) {
      onApplied?.();
    }
    return isApplied;
  };

  const applyWithRachelTransition = async ({
    nextItems,
    apply,
  }: ApplyRachelListTransitionParams<T>) => {
    if (!scope || !useRachelChangesStore.getState().isChangeActive(scope)) {
      applyAndNotify(apply);
      return;
    }

    const { addedItems, removedKeys } = diffItems({ previousItems: items, nextItems, getKey });

    if (removedKeys.length) {
      await animateRemoval(scope, removedKeys);
    }

    if (applyAndNotify(apply) && addedItems.length) {
      markAddedKeys(scope, addedItems.map(getKey));
      onAdded?.(addedItems);
    }
  };

  return { applyWithRachelTransition };
};
